import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { Probe, ProbeForm, ProbeSearchForm } from 'entities/probe'

type ProbesResponse = PagingResponse & {
    results: Probe[]
}

export function useFetchProbesApi(searchForm: Form<ProbeSearchForm>): IndexApiSet<ProbesResponse> & { execute: () => void; executeAndPageReset: () => void } {
    const apiPath = 'probes/'
    const api = useIndexApi<ProbesResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm.object })
    }

    const executeAndPageReset = (): void => {
        if (api.pageSet.page == 1) {
            execute()
        } else {
            api.pageSet.setPage(1)
        }
    }

    useEffectSkipFirst(() => {
        execute()
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        executeAndPageReset()
    }, [searchForm.object])

    return { ...api, execute: execute, executeAndPageReset: executeAndPageReset }
}

export function useDownloadCsvProbesApi(searchForm: Form<ProbeSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'probes/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'probe', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostProbeApi(): ApiSet<BaseResponse> & { execute: (form: Form<ProbeForm>) => void } {
    const apiSet = usePostApi<BaseResponse, ProbeForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<ProbeForm>) => {
        const apiPath = `probes/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutProbeApi(): ApiSet<BaseResponse> & { execute: (form: Form<ProbeForm>) => void } {
    const apiSet = usePutApi<BaseResponse, ProbeForm>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<ProbeForm>) => {
        const apiPath = `probes/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutProbesApi(): ApiSet<BaseResponse> & { execute: (form: Form<ProbeForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, ProbeForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<ProbeForm[]>) => {
        const apiPath = `probes/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteProbeApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `probes/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
